import actions from 'redux/actions'
import { constants, sentry } from 'common/helpers'
import { applyOptions, mockRequest, DummyPromise, token } from './util'


// Set for active request states
const activeOnceRequests = new Map()

const defaultOptions = {
  sameOrigin: true,
  multiple: false,
  withLoader: false,
  withErrorModal: true,
  modifyResult: (resData) => resData,
  onComplete: () => {},
}


let showTimer
let hideTimer
let isVisible = false
let requestCount = 0

const showLoader = () => {
  requestCount++

  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }

  if (!showTimer && !isVisible) {
    showTimer = setTimeout(() => {
      showTimer = null
      isVisible = true
      actions.models.ui.showRequestLoader()
    }, 100)
  }
}

const hideLoader = () => {
  requestCount--

  if (showTimer && requestCount === 0) {
    clearTimeout(showTimer)
    showTimer = null
  }

  if (!showTimer && isVisible && requestCount === 0) {
    hideTimer = setTimeout(() => {
      hideTimer = null
      isVisible = false
      actions.models.ui.hideRequestLoader()
    }, 100)
  }
}

const resolveToken = (opts, body, statusCode) => {
  if (opts.tokenName && body) {
    if (body.token) {
      token.update(opts.tokenName, body.token)
    }
    else if (statusCode < 500 && statusCode !== 400) {
      sentry.captureException(`Token "${opts.tokenName}" required for "${opts.method.toUpperCase()} ${opts.endpoint}"`)
    }
  }
}


const createResponseHandler = (req, opts) => {
  const debug = `${opts.method.toUpperCase()} ${opts.endpoint}`
  const isGQL = /graphql/.test(opts.endpoint)

  return new Promise((resolve, reject) => req.end((err, res) => {
    let serverError

    const body        = res  && res.body
    const message     = body && body.message
    const statusCode  = res  && res.statusCode // eslint-disable-line

    activeOnceRequests.delete(opts.cacheKey)
    resolveToken(opts, body, statusCode)

    if (opts.withLoader) {
      hideLoader()
    }

    // Errors

    if (err) {
      console.error(`Response err for ${opts.endpoint}:`, err)

      // if user logout in new browser tab we need reload page
      if (message && message === 'Unauthorized' && !/register\/log-in/g.test(window.location.pathname)) {
        window.location.reload()

        return resolve(new DummyPromise())
      }
    }

    if (!res && !err) {
      serverError = `Connection failed: ${debug}`
    }
    else if (!res || statusCode >= 500) {
      serverError = 'We`re having technical issues at that moment. Please try again later'
    }

    if (serverError) {
      if (opts.withHumbleErrorModal) {
        actions.models.modals.open(constants.modals.humble.ServerError)
      }
      else if (opts.withErrorModal) {
        actions.models.modals.open(constants.modals.SomethingWentWrong)
      }

      reject({ resData: {}, err, message: serverError, statusCode, res })

      return
    }

    // TODO add condition to show "Something went wrong" for statusCode !== 428 - Wrong Token
    // rest error with message
    if (
      err && message
      && statusCode !== 401
    ) {
      const { title, message } = body

      if (opts.withHumbleErrorModal) {
        actions.models.modals.open(constants.modals.humble.ServerError, { title, text: message })
      }
      else if (opts.withErrorModal) {
        actions.models.modals.open(constants.modals.ServerError, { title, content: message })
      }
    }

    // graphql error without data
    // TODO ask backend about message for user?
    if (
      body && !body.data && body.errors
    ) {
      if (opts.withHumbleErrorModal) {
        actions.models.modals.open(constants.modals.humble.ServerError)
      }
      else if (opts.withErrorModal) {
        actions.models.modals.open(constants.modals.SomethingWentWrong)
      }
    }

    if (err) {
      const { error, message, title, status, ...resData } = (body || {})

      reject({ err, res, error, message, title, statusCode: status || statusCode, resData })
    }
    else {
      let { data, errors } = body
      const key = Object.keys(data)[0]

      if (data[key] && data[key].errors) {
        errors = data[key].errors // eslint-disable-line
      }

      const resData = opts.modifyResult(data || {})

      if (errors) {
        reject({ errors, resData })
      }
      else {
        resolve(resData)
      }
    }

    if (typeof opts.onComplete === 'function') {
      opts.onComplete()
    }
  }))
}

const logToConsole = (opts) => {
  const isGraphQL = opts.endpoint === 'graphql'
  const method    = opts.method.toUpperCase()

  const logs = [
    `%c[request] %c${method} /${opts.endpoint}`,
    'color: #9102e5;',
    'font-weight: bold; color: #9102e5;',
  ]

  console.log(...logs)

  if (isGraphQL) {
    console.groupCollapsed('query')
    console.log(opts.body.query)
    console.groupEnd()

    if (opts.body.variables) {
      console.groupCollapsed('variables')
      console.log(opts.body.variables)
      console.groupEnd()
    }
  }
  else {
    if (opts.query) {
      console.groupCollapsed('query')
      console.log(opts.query)
      console.groupEnd()
    }
    if (opts.body) {
      console.groupCollapsed('body')
      console.log(opts.body)
      console.groupEnd()
    }
  }
}

/**
 *
 * @param {Object} options
 * @param {String} options.endpoint
 * @param {String} options.method
 * @param {Object} options.headers
 * @param {Object} options.query
 * @param {Object} options.body
 * @param {String} options.tokenName
 * @param {Boolean} options.withCredentials
 * @param {Boolean} options.multiple - if request can be called multiple times
 * @param {String} options.mockError
 * @param {Object} options.mockResponse
 * @param {number} options.mockDelay
 *
 * @returns {Promise<any>}
 */
const sendRequest = (options) => {
  const opts = { ...defaultOptions, ...options }
  const isGraphQL = options.endpoint === 'graphql'

  if (isGraphQL) {
    opts.multiple = true
  }

  if (window.debug) {
    logToConsole(opts)
  }

  if (!opts.multiple) {
    opts.cacheKey = `${opts.method}:${opts.endpoint}:${opts.query ? JSON.stringify(opts.query) : ''}:${opts.body ? JSON.stringify(opts.body) : ''}`

    if (activeOnceRequests.has(opts.cacheKey)) {
      // return empty methods to prevent errors
      return activeOnceRequests.get(opts.cacheKey)
    }
  }

  if (opts.withLoader) {
    showLoader()
  }

  if (typeof opts.onStart === 'function') {
    opts.onStart()
  }

  let req

  if (opts.mockError || opts.mockResponse || opts.mockDelay) {
    req = mockRequest(opts)
  }
  else {
    req = applyOptions(opts)
  }

  const responseHandler = createResponseHandler(req, opts)

  responseHandler.abort = req.abort.bind(req)

  if (!opts.multiple) {
    activeOnceRequests.set(opts.cacheKey, responseHandler)
  }

  return responseHandler
}

/**
 * @param {string} method
 * @returns {function}
*/
const requestByMethod = (method) =>
  /**
   * @param {string} endpoint
   * @param {*} opts
   * @returns {Promise<any>}
   */
  (endpoint, opts) => sendRequest({ ...opts, endpoint, method })


export default {
  post: requestByMethod('post'),
}
